import { reportDiagnostic } from '../platform/telemetry';
import { MAX_DIAGNOSTIC_DURATION_MS } from '../../shared/diagnostics';
import { CLOSE_CODES, isServerMessage, type ClientMessage, type ServerMessage } from '../../shared/protocol';
import { roomSocketPath } from '../../shared/routes';
import { socketUrl } from '../platform/socket-url';

interface ConnectionEvents {
  open(): void;
  close(code: number): void;
  message(message: ServerMessage): void;
  clock(offset: number): void;
}

/** First reconnect delay; each failure multiplies it by `RECONNECT_BACKOFF` up to `RECONNECT_MAX_MS`. */
const RECONNECT_INITIAL_MS = 400;
const RECONNECT_BACKOFF = 1.7;
const RECONNECT_MAX_MS = 4_000;
/** A connect or pong this late means the network path is dead; reconnect instead of waiting. */
const RESPONSE_TIMEOUT_MS = 10_000;
/** Clock resync interval while connected. */
const CLOCK_SYNC_MS = 30_000;
/** A short burst is enough for a minimum-RTT sample; every ping wakes the room object. */
const CLOCK_PINGS = 3;
const CLOCK_PING_GAP_MS = 90;

/** Transport only: room credentials and authoritative snapshots belong to the app. */
export class RoomConnection {
  private socket: WebSocket | null = null;
  private code = '';
  private generation = 0;
  private running = false;
  private delay = RECONNECT_INITIAL_MS;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private connectTimeout?: ReturnType<typeof setTimeout>;
  private pingTimer?: ReturnType<typeof setTimeout>;
  private syncTimer?: ReturnType<typeof setInterval>;
  private pongTimeout?: ReturnType<typeof setTimeout>;
  private bestRtt = Infinity;

  constructor(private readonly events: ConnectionEvents) {}

  isOpen(code = this.code): boolean {
    return code === this.code && this.socket?.readyState === WebSocket.OPEN;
  }

  connect(code: string): void {
    if (this.code !== code) this.stop();
    this.code = code;
    this.running = true;
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    clearTimeout(this.reconnectTimer);
    const generation = ++this.generation;
    const started = Date.now();
    const socket = new WebSocket(socketUrl(roomSocketPath(code)));
    this.socket = socket;
    this.connectTimeout = setTimeout(() => {
      if (generation !== this.generation || this.socket !== socket || socket.readyState !== 0) return;
      reportDiagnostic({ kind: 'connection_timeout' });
      this.socket = null;
      this.generation++;
      socket.close();
      this.events.close(CLOSE_CODES.abnormal);
      this.scheduleReconnect();
    }, RESPONSE_TIMEOUT_MS);
    socket.onopen = () => {
      if (generation !== this.generation) return;
      clearTimeout(this.connectTimeout);
      reportDiagnostic({
        kind: 'connection_latency',
        durationMs: Math.min(MAX_DIAGNOSTIC_DURATION_MS, Math.max(0, Date.now() - started)),
      });
      this.delay = RECONNECT_INITIAL_MS;
      this.events.open();
      this.syncClock();
      clearInterval(this.syncTimer);
      this.syncTimer = setInterval(() => this.syncClock(), CLOCK_SYNC_MS);
    };
    socket.onmessage = (event) => {
      if (generation !== this.generation) return;
      let message: unknown;
      try {
        message = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (!isServerMessage(message)) return;
      if (message.t === 'pong') {
        clearTimeout(this.pongTimeout);
        const now = Date.now();
        const rtt = now - message.c;
        if (rtt >= 0 && rtt < this.bestRtt) {
          this.bestRtt = rtt;
          this.events.clock(message.s + rtt / 2 - now);
        }
      } else this.events.message(message);
    };
    socket.onclose = (event) => {
      if (generation !== this.generation) return;
      this.socket = null;
      clearTimeout(this.connectTimeout);
      clearTimeout(this.pingTimer);
      clearTimeout(this.pongTimeout);
      clearInterval(this.syncTimer);
      const replaced = event.code === CLOSE_CODES.sessionReplaced;
      if (event.code !== CLOSE_CODES.normal && !replaced) reportDiagnostic({ kind: 'connection_closed' });
      if (replaced) this.running = false;
      this.events.close(event.code);
      this.scheduleReconnect();
    };
    socket.onerror = () => reportDiagnostic({ kind: 'connection_error' });
  }

  send(message: ClientMessage): boolean {
    if (!this.socket || !this.isOpen()) return false;
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  }

  syncClock(): void {
    clearTimeout(this.pingTimer);
    clearTimeout(this.pongTimeout);
    if (!this.isOpen()) return;
    this.pongTimeout = setTimeout(() => {
      reportDiagnostic({ kind: 'connection_timeout' });
      const code = this.code;
      this.stop();
      this.events.close(CLOSE_CODES.abnormal);
      this.connect(code);
    }, RESPONSE_TIMEOUT_MS);
    this.bestRtt = Infinity;
    let count = 0;
    const ping = () => {
      if (!this.isOpen() || count++ >= CLOCK_PINGS) return;
      this.send({ t: 'ping', c: Date.now() });
      this.pingTimer = setTimeout(ping, CLOCK_PING_GAP_MS);
    };
    ping();
  }

  private scheduleReconnect(): void {
    if (!this.running) return;
    this.reconnectTimer = setTimeout(() => this.connect(this.code), this.delay);
    this.delay = Math.min(this.delay * RECONNECT_BACKOFF, RECONNECT_MAX_MS);
  }

  stop(): void {
    this.running = false;
    this.generation++;
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.connectTimeout);
    clearTimeout(this.pingTimer);
    clearTimeout(this.pongTimeout);
    clearInterval(this.syncTimer);
    this.socket?.close();
    this.socket = null;
  }
}
